import {
  faPlus,
  faPencil,
  faTrash,
  faExclamationTriangle,
  faTag,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
} from "@tanstack/react-table";
import Button from "components/base/Button";
import AdvanceTable from "components/base/AdvanceTable";
import AdvanceTableFooter from "components/base/AdvanceTableFooter";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { useState } from "react";
import { Alert, Card, Modal, Placeholder, Spinner, Table } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "providers/ToastProvider";
import {
  useTabelasPreco,
  useRemoverTabelaPreco,
} from "hooks/useTabelaPreco";
import {
  TabelaPrecoListItem,
  TipoCalculoTabelaPreco,
} from "services/tabelaPrecoService";

// ─── Breadcrumb ────────────────────────────────────────────────────────────────

const breadcrumbItems = [
  { label: "Cadastros", to: "/cadastros" },
  { label: "Tabela de Preços", active: true },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

const tipoCalculoLabels: Record<TipoCalculoTabelaPreco, string> = {
  [TipoCalculoTabelaPreco.AcrescimoSobreVenda]: "Acréscimo sobre o preço de venda",
  [TipoCalculoTabelaPreco.DescontoSobreVenda]: "Desconto sobre o preço de venda",
  [TipoCalculoTabelaPreco.MarkupSobreCusto]: "Markup sobre o preço de custo",
  [TipoCalculoTabelaPreco.MargemLucro]: "Margem de lucro",
  [TipoCalculoTabelaPreco.ValorFixo]: "Valor fixo",
};

// ─── Modal de confirmação de remoção (autocontido) ─────────────────────────────

interface ModalRemoverTabelaProps {
  tabela: TabelaPrecoListItem | null;
  onClose: () => void;
}

const ModalRemoverTabela = ({ tabela, onClose }: ModalRemoverTabelaProps) => {
  const removerMutation = useRemoverTabelaPreco();
  const { showToast } = useToast();

  const handleConfirm = async () => {
    if (!tabela) return;
    try {
      await removerMutation.mutateAsync(tabela.id);
      showToast("Tabela de preços removida com sucesso", "success");
      onClose();
    } catch {
      // erro tratado pelo onError do hook
    }
  };

  const handleClose = () => {
    removerMutation.reset();
    onClose();
  };

  return (
    <Modal show={!!tabela} onHide={handleClose} centered size="lg" className="fade">
      <Modal.Header className="border-0 pb-0">
        <div className="d-flex justify-content-center w-100">
          <div className="rounded-circle bg-danger bg-opacity-10 p-3 mb-3">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-danger fa-2x"
            />
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="text-center px-4 pt-0">
        {removerMutation.isError && (
          <Alert
            variant="danger"
            className="border border-danger border-opacity-25 bg-danger-subtle mb-3"
          >
            <div className="text-danger-emphasis">
              <strong>Erro:</strong> Não foi possível remover a tabela de preços.
              Tente novamente.
            </div>
          </Alert>
        )}

        <h4 className="mb-3">Confirmar Remoção</h4>
        <p className="mb-3">Você está prestes a remover a tabela de preços:</p>

        <div className="d-flex align-items-center justify-content-center mb-4">
          <div className="border rounded-4 py-3 px-4 bg-body">
            <div className="d-flex align-items-center">
              <div className="rounded-circle bg-secondary bg-opacity-10 p-2 me-3 shadow-sm">
                <FontAwesomeIcon icon={faTag} className="text-secondary fa-2x" />
              </div>
              <div className="text-start">
                <h5 className="mb-0">{tabela?.nome}</h5>
                <div className="d-flex align-items-center text-muted">
                  <small className="me-2">Código:</small>
                  <span className="badge bg-secondary">#{tabela?.codigo}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-body border-start border-4 border-warning rounded p-3 mb-3 text-start">
          <h6 className="d-flex align-items-center mb-2">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-warning" />
            Informações Importantes
          </h6>
          <p className="text-muted small mb-0">
            Ao remover esta tabela de preços, ela deixará de estar disponível para
            associação com empresas e produtos. Esta ação não pode ser desfeita.
          </p>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 justify-content-center pt-0">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={removerMutation.isPending}
          className="px-4"
        >
          Cancelar
        </Button>
        <Button
          variant="danger"
          onClick={handleConfirm}
          disabled={removerMutation.isPending}
          className="px-4"
        >
          {removerMutation.isPending ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Removendo...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faTrash} className="me-2" />
              Remover Tabela
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── Colunas da tabela ─────────────────────────────────────────────────────────

const buildColumns = (
  onEditar: (id: string) => void,
  onRemover: (tabela: TabelaPrecoListItem) => void
): ColumnDef<TabelaPrecoListItem>[] => [
  {
    accessorKey: "codigo",
    header: "Código",
    cell: ({ getValue }) => (
      <span className="text-body-emphasis">#{getValue<number>()}</span>
    ),
    size: 80,
  },
  {
    accessorKey: "nome",
    header: "Nome",
    cell: ({ row: { original } }) => (
      <Link
        to={`/cadastros/tabela-preco/${original.id}`}
        className="fw-semibold line-clamp-3"
      >
        {original.nome}
      </Link>
    ),
    meta: {
      headerProps: { style: { width: 300 }, className: "ps-4" },
      cellProps: { className: "ps-4" },
    },
  },
  {
    accessorKey: "tipoCalculo",
    header: "Tipo de cálculo",
    cell: ({ getValue }) => (
      <span className="text-body-secondary">
        {tipoCalculoLabels[getValue<TipoCalculoTabelaPreco>()] ?? getValue<number>()}
      </span>
    ),
  },
  {
    accessorKey: "qtdProdutos",
    header: "Produtos",
    cell: ({ getValue }) => {
      const qtd = getValue<number>();
      return qtd > 0 ? (
        <span className="badge bg-primary-subtle text-primary rounded-pill">
          {qtd} {qtd === 1 ? "produto" : "produtos"}
        </span>
      ) : (
        <span className="text-body-tertiary">—</span>
      );
    },
    size: 110,
  },
  {
    accessorKey: "ativa",
    header: "Situação",
    cell: ({ getValue }) =>
      getValue<boolean>() ? (
        <span className="badge bg-success-subtle text-success rounded-pill">
          Ativa
        </span>
      ) : (
        <span className="badge bg-secondary-subtle text-body-secondary rounded-pill">
          Inativa
        </span>
      ),
    size: 100,
  },
  {
    id: "acoes",
    header: "Ações",
    cell: ({ row: { original } }) => (
      <div className="d-flex gap-2">
        <Button
          variant="phoenix-secondary"
          size="sm"
          className="px-2"
          title="Editar"
          onClick={() => onEditar(original.id)}
        >
          <FontAwesomeIcon icon={faPencil} />
        </Button>
        <Button
          variant="phoenix-danger"
          size="sm"
          className="px-2"
          title="Remover"
          onClick={() => onRemover(original)}
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </div>
    ),
    size: 100,
  },
];

// ─── Página principal ──────────────────────────────────────────────────────────

const TabelasPreco = () => {
  const navigate = useNavigate();
  const dataQuery = useTabelasPreco();
  const [tabelaParaRemover, setTabelaParaRemover] =
    useState<TabelaPrecoListItem | null>(null);

  const columns = buildColumns(
    (id) => navigate(`/cadastros/tabela-preco/${id}`),
    (tabela) => setTabelaParaRemover(tabela)
  );

  const table = useReactTable({
    data: dataQuery.data ?? [],
    columns,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    initialState: { pagination: { pageSize: 10 } },
  });

  if (dataQuery.isLoading) {
    return (
      <div>
        <PageBreadcrumb items={breadcrumbItems} />
        <div className="mb-9">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">Tabela de Preços</h2>
            <Placeholder.Button variant="primary" xs={2} className="px-4 py-2" />
          </div>

          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <div className="mx-n4 px-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent">
                <Table className="phoenix-table fs-9 mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: "10%" }}>Código</th>
                      <th>Nome</th>
                      <th>Tipo de cálculo</th>
                      <th style={{ width: "11%" }}>Produtos</th>
                      <th style={{ width: "12%" }}>Situação</th>
                      <th style={{ width: "10%" }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}>
                        <td><Placeholder animation="wave" as="div"><Placeholder xs={5} /></Placeholder></td>
                        <td><Placeholder animation="wave" as="div"><Placeholder xs={8} /></Placeholder></td>
                        <td><Placeholder animation="wave" as="div"><Placeholder xs={9} /></Placeholder></td>
                        <td><Placeholder animation="wave" as="div"><Placeholder xs={5} /></Placeholder></td>
                        <td><Placeholder animation="wave" as="div"><Placeholder xs={6} /></Placeholder></td>
                        <td><Placeholder animation="wave" as="div"><Placeholder xs={4} /></Placeholder></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  if (dataQuery.isError) {
    return (
      <div className="text-center py-5 text-danger">
        {(dataQuery.error as Error).message}
      </div>
    );
  }

  return (
    <div>
      <PageBreadcrumb items={breadcrumbItems} />
      <div className="mb-9">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Tabela de Preços</h2>
          <Button
            variant="primary"
            as={Link}
            to="/cadastros/tabela-preco/nova"
            className="px-4 py-2"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Nova Tabela de Preços
          </Button>
        </div>

        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <AdvanceTableProvider {...table}>
              <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                <AdvanceTable tableProps={{ className: "phoenix-table fs-9" }} />
                <AdvanceTableFooter pagination showViewAllBtn={false} />
              </div>
            </AdvanceTableProvider>
          </Card.Body>
        </Card>
      </div>

      <ModalRemoverTabela
        tabela={tabelaParaRemover}
        onClose={() => setTabelaParaRemover(null)}
      />
    </div>
  );
};

export default TabelasPreco;
